import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useMensagens } from "@/context/MensagensContext";

// Lote 152 — conversa rápida da equipe, agora dentro da gaveta lateral (aba "Equipe"). É o mesmo chat do balão
// que existia nos Lotes 91/94 (mesmas rotas e mesmos data-testid "msg-rapida-*"); só mudou o lugar onde aparece.
// Rascunho e histórico daqui NUNCA se misturam com a aba Assistente IA: são componentes e estados separados.
const fmtHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toDateString() === new Date().toDateString()
      ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
};

export default function ChatRapido({ ativo, aoNavegar }) {
  const navigate = useNavigate();
  const { unread, convites, ultima, refresh } = useMensagens();
  const [threads, setThreads] = useState(null);
  const [tid, setTid] = useState(null);
  const [conv, setConv] = useState(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const fimRef = useRef(null);

  const loadThreads = useCallback(() => http.get("/msg/threads").then((r) => setThreads(r.data)).catch(() => setThreads((t) => t || [])), []);
  const loadConv = useCallback(async () => {
    if (!tid) { setConv(null); return; }
    try { const r = await http.get(`/msg/threads/${tid}`); setConv(r.data); refresh(); } catch { setConv(null); }
  }, [tid, refresh]);

  // Ao aparecer a aba: vai direto à conversa não lida mais recente, se houver.
  useEffect(() => {
    if (!ativo) return;
    setTid(unread > 0 && ultima?.thread_id ? ultima.thread_id : null);
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo]);
  useEffect(() => { if (ativo) loadConv(); }, [ativo, loadConv]);
  useEffect(() => {
    if (!ativo) return undefined;
    const id = setInterval(() => { if (document.visibilityState !== "hidden") { loadThreads(); if (tid) loadConv(); } }, 8000);
    return () => clearInterval(id);
  }, [ativo, tid, loadThreads, loadConv]);
  useEffect(() => { fimRef.current?.scrollIntoView({ block: "end" }); }, [conv?.messages?.length, tid]);

  const enviar = async () => {
    if (!conv || enviando || !texto.trim()) return;
    setEnviando(true);
    try {
      await http.post(`/msg/threads/${conv.thread.thread_id}/mensagens`, { text: texto, anexo: null });
      setTexto("");
      await loadConv(); loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar");
    } finally { setEnviando(false); }
  };

  const irPara = (to) => { aoNavegar?.(); navigate(to); };
  const irParaPagina = () => irPara(tid ? `/mensagens?t=${tid}` : unread === 0 && convites > 0 ? "/mensagens?aba=vinculos" : "/mensagens");

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="msg-rapida">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 bg-stone-50">
        {tid && (
          <button type="button" data-testid="msg-rapida-voltar" onClick={() => setTid(null)} aria-label="Voltar às conversas" className="p-1 text-stone-500 hover:text-stone-900">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="min-w-0 flex-1 text-sm font-semibold text-stone-900 truncate" data-testid="msg-rapida-titulo">
          {tid ? (conv?.thread?.title || "Conversa") : "Conversas da equipe"}
        </div>
        <button type="button" data-testid="msg-rapida-abrir" onClick={irParaPagina} className="inline-flex items-center gap-1 text-xs font-semibold text-stone-700 bg-white hover:bg-stone-100 border border-stone-300 rounded-lg px-2.5 h-8">
          Abrir em Mensagens <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      {!tid ? (
        <div className="overflow-y-auto flex-1" data-testid="msg-rapida-lista">
          {convites > 0 && (
            <button type="button" onClick={() => irPara("/mensagens?aba=vinculos")} className="w-full text-left px-3 py-2.5 text-xs font-semibold bg-[#eaf5ff] text-[#0b4f94] border-b border-stone-100">
              {convites} {convites === 1 ? "convite de vínculo aguardando" : "convites de vínculo aguardando"} — ver
            </button>
          )}
          {threads === null ? (
            <p className="p-5 text-xs text-stone-400 text-center">Carregando…</p>
          ) : threads.length === 0 ? (
            <p className="p-5 text-xs text-stone-400 text-center">Nenhuma conversa ainda. Use "Abrir em Mensagens" para começar uma.</p>
          ) : threads.slice(0, 12).map((t) => (
            <button key={t.thread_id} type="button" data-testid={`msg-rapida-thread-${t.thread_id}`} onClick={() => setTid(t.thread_id)}
              className="w-full text-left px-3 py-2.5 border-b border-stone-100 hover:bg-stone-50 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className={`text-sm truncate ${t.unread ? "font-bold text-stone-900" : "font-medium text-stone-800"}`}>{t.title}</div>
                <div className="text-xs text-stone-500 truncate">{t.last_preview || "—"}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[11px] text-stone-400 tabular-nums">{fmtHora(t.last_at)}</span>
                {t.unread > 0 && <span data-testid={`msg-rapida-unread-${t.thread_id}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#c8502d] text-white text-[11px] font-bold inline-flex items-center justify-center">{t.unread}</span>}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[160px]" data-testid="msg-rapida-conversa">
            {!conv ? <p className="text-xs text-stone-400 text-center mt-4">Carregando…</p> : conv.messages.slice(-30).map((m) => (
              <div key={m.msg_id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm ${m.mine ? "bg-[#d9fdd3]" : "bg-stone-100"} text-stone-900`}>
                  {!m.mine && conv.thread.kind === "group" && <div className="text-[11px] font-semibold text-emerald-brand">{m.sender_name}</div>}
                  {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                  {m.anexo && <div className="mt-1 text-xs rounded border border-stone-300/70 bg-white/70 px-2 py-1 break-words">{m.anexo.titulo}</div>}
                  <div className="text-[10px] mt-0.5 text-stone-500 tabular-nums">{fmtHora(m.created_at)}</div>
                </div>
              </div>
            ))}
            <div ref={fimRef} />
          </div>
          <div className="p-2.5 border-t border-stone-200 bg-white flex items-end gap-2">
            <textarea
              data-testid="msg-rapida-texto" rows={2} maxLength={2000} value={texto} onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              placeholder="Responder à equipe… (Enter envia)"
              className="flex-1 resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
            />
            <button type="button" data-testid="msg-rapida-enviar" disabled={enviando || !texto.trim()} onClick={enviar} aria-label="Enviar"
              className="h-10 w-10 rounded-lg bg-emerald-brand text-white inline-flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
